import { components, type GroupBase, type InputProps } from 'react-select';

import { useInputConfiguration } from './hooks/input-configuration.hook';

/**
 * Custom Input component for react-select that integrates additional paste handling logic.
 *
 * This component wraps the default `Input` from `react-select` and uses the `useInputConfiguration`
 * hook to determine if pasting is allowed and to provide a custom `onPaste` handler.
 *
 * @template Option - The option type.
 * @template IsMulti - Whether multiple values are allowed.
 * @template Group - The group type for options.
 *
 * @param props - The input props provided by react-select, including `selectProps`.
 * @returns The customized Input component with conditional paste handling.
 */
function Input<Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  selectProps,
  ...props
}: InputProps<Option, IsMulti, Group>) {
  const { canPaste, onPaste } = useInputConfiguration({ selectProps });

  return (
    <components.Input
      {...props}
      onPaste={canPaste ? onPaste : undefined}
      selectProps={selectProps}
    />
  );
}

export default Input;
